import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  Calendar, 
  MessageSquare, 
  Film, 
  Clock, 
  ArrowRight,
  CheckCircle,
  BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const { currentTheme } = useTheme();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [sessionsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/coaching/my-sessions`, { withCredentials: true }),
        axios.get(`${API}/messages`, { withCredentials: true })
      ]);
      setSessions(sessionsRes.data.slice(0, 3));
      setMessages(messagesRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { 
      icon: Calendar, 
      label: 'Book Session', 
      href: '/coaching',
      color: currentTheme.primary 
    },
    { 
      icon: MessageSquare, 
      label: 'Messages', 
      href: '/messages',
      color: currentTheme.secondary 
    },
    { 
      icon: Film, 
      label: 'Media Library', 
      href: '/media',
      color: currentTheme.accent 
    },
    { 
      icon: BookOpen, 
      label: 'Our Apps', 
      href: '/apps',
      color: '#E07A5F' 
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#2A9D8F';
      case 'pending': return '#E9C46A';
      case 'completed': return currentTheme.primary;
      case 'cancelled': return '#E76F51';
      default: return currentTheme.muted;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Welcome Section */}
          <div className="mb-12">
            <h1 className="font-display text-3xl md:text-4xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="font-body text-lg opacity-70">
              Continue your journey to freedom
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {quickActions.map((action) => (
              <Link 
                key={action.label}
                to={action.href}
                className="stat-card flex flex-col items-center justify-center py-8 text-center card-hover"
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${action.color}20` }}
                >
                  <action.icon className="w-7 h-7" style={{ color: action.color }} />
                </div>
                <span className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                  {action.label}
                </span>
              </Link>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Upcoming Sessions */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-medium" style={{ color: currentTheme.foreground }}>
                  Your Sessions
                </h2>
                <Link to="/coaching" className="font-body text-sm flex items-center gap-1" style={{ color: currentTheme.primary }}>
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-20 rounded-xl" />
                  ))}
                </div>
              ) : sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <div 
                      key={session.session_id}
                      className="p-4 rounded-xl border"
                      style={{ borderColor: currentTheme.border }}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                            {session.topic}
                          </h3>
                          <div className="flex items-center gap-4 mt-2 text-sm opacity-60">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {session.preferred_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.preferred_time}
                            </span>
                          </div>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium capitalize"
                          style={{ 
                            backgroundColor: `${getStatusColor(session.status)}20`,
                            color: getStatusColor(session.status)
                          }}
                        >
                          {session.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="font-body opacity-60 mb-4">No sessions booked yet</p>
                  <Link to="/coaching">
                    <Button style={{ backgroundColor: currentTheme.primary }}>
                      Book Your First Session
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Recent Messages */}
            <div 
              className="rounded-2xl p-6"
              style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-medium" style={{ color: currentTheme.foreground }}>
                  Recent Messages
                </h2>
                <Link to="/messages" className="font-body text-sm flex items-center gap-1" style={{ color: currentTheme.primary }}>
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-20 rounded-xl" />
                  ))}
                </div>
              ) : messages.length > 0 ? (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div 
                      key={message.message_id}
                      className="p-4 rounded-xl border"
                      style={{ borderColor: currentTheme.border }}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${currentTheme.primary}20` }}
                        >
                          <span style={{ color: currentTheme.primary }}>
                            {message.sender_name?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                              {message.sender_name}
                            </span>
                            {!message.is_read && (
                              <span 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: currentTheme.primary }}
                              />
                            )}
                          </div>
                          <p className="font-body text-sm opacity-70 truncate">{message.subject}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p className="font-body opacity-60 mb-4">No messages yet</p>
                  <Link to="/messages">
                    <Button style={{ backgroundColor: currentTheme.primary }}>
                      Start a Conversation
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Progress Section */}
          <div 
            className="mt-8 rounded-2xl p-8"
            style={{ 
              background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent})`,
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white">
                <h2 className="font-display text-2xl font-medium mb-2">
                  Ready to Level Up?
                </h2>
                <p className="font-body opacity-90">
                  Explore our media library for exclusive content on achieving freedom.
                </p>
              </div>
              <Link to="/media">
                <Button 
                  className="bg-white hover:bg-white/90 px-8"
                  style={{ color: currentTheme.primary }}
                >
                  Explore Content
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
